// v4 named mutations — the wire finally says what the USER DID, not what the
// snapshot diff looked like.
//
// The one that pays for the whole step is `moveClip`: under v3 a move compiled
// as deleteClip + upsertClip(id:null), which (a) could half-apply — the
// destruction hole steps 1/3b/4 progressively closed — and (b) minted a new
// clip id with fresh audio, so every drag re-rendered and re-uploaded bytes it
// already had. A moveClip names the clip and the new position, CANNOT
// half-apply (it is one operation), and never touches content, so it never
// triggers an audio refresh.
//
// ONE MERGE CORE, deliberately: a mutation is TRANSLATED into a v3 intent
// against the current room (moveClip/editClip read the clip's canonical
// content and re-assert it with the change applied) and executed by the same
// `mergeIntents` the v3 wire uses — validation, overlap rules, gesture trials
// and rev bookkeeping exist once. The doc's `executeMutation` is this
// translate-then-merge pair; a parallel executor would be the jam-sim README's
// mirror-drift bug class all over again.
//
// The REQUEST envelope is the only v4 shape. Broadcasts stay v3 (`ops`), so
// viz's `v === 3` guard holds and v3 senders (viz, older extensions) are
// supported indefinitely. Deploy order matters the usual way: server first
// (beta channel), because an old server answers a v4 envelope with a 400.

import { withMidiPayload } from "./compile-intents.js";
import type { Intent, Rejection } from "./intents.js";
import type { WireNote } from "../midi/notes.js";
import { type ClipData, type ClipKind, warpSettingsOf } from "./protocol.js";
import type { CanonicalRoom, ChainOwner, DeviceState, TrackField, TrackFieldValue, TrackKind, ScaleState } from "./state.js";

export type Mutation =
  | { kind: "createTrack"; tempId: string; name: string; ord: number; trackKind?: TrackKind }
  | {
      kind: "setTrackField";
      trackId: string;
      field: TrackField;
      value: TrackFieldValue;
      baseRev: number;
    }
  | { kind: "deleteTrack"; trackId: string; baseRev: number }
  | { kind: "setTrackOrder"; trackId: string; ord: number; baseRev: number }
  | { kind: "setSceneNames"; names: Array<string | null>; baseRev: number }
  | { kind: "setCuePoints"; points: Array<{ name: string; time: number }>; baseRev: number }
  | { kind: "setMaster"; volume: number; pan: number; baseRev: number }
  | { kind: "setScale"; scale: ScaleState; baseRev: number }
  | { kind: "setTrackSends"; trackId: string; sends: number[]; baseRev: number }
  | { kind: "setTrackDevices"; trackId: string; devices: DeviceState[]; baseRev: number; origin?: "user" | "reconcile" }
  | { kind: "setDeviceParam"; trackId: string; device: number; param: number; value: number; baseRev: number }
  | { kind: "setChain"; owner: ChainOwner; devices: DeviceState[]; baseRev: number; origin?: "user" | "reconcile" }
  | { kind: "setChainParam"; owner: ChainOwner; device: number; param: number; value: number; baseRev: number }
  | { kind: "setReturns"; returns: Array<{ name: string; mute: boolean; solo: boolean; volume: number; pan: number }>; baseRev: number }
  | {
      kind: "createClip";
      tempId: string;
      trackId: string;
      clip: ClipData;
      /** Content-addressed R2 key. Required for an AUDIO clip — a clip with no
       * resolvable audio is not a clip; null only transiently, before the
       * audio fill. Optional for a MIDI clip (an audibility bounce). */
      audioKey: string | null;
    }
  | {
      kind: "moveClip";
      clipId: string;
      startTime: number;
      /** Present only for a cross-lane move. */
      trackId?: string;
      /**
       * Session View placement. A number moves the clip into that clip slot
       * (startTime is then 0 by convention); `null` moves it onto the
       * timeline at `startTime`; absent keeps whichever view the clip is in
       * (old clients, which only ever move along the timeline).
       */
      slot?: number | null;
      baseRev: number;
    }
  | {
      kind: "editClip";
      clipId: string;
      patch: Partial<ClipData>;
      /** Only when the edit changed audible content — geometry/marker edits
       * re-render; a rename does not. */
      audioKey?: string;
      baseRev: number;
    }
  | { kind: "deleteClip"; clipId: string; baseRev: number }
  | { kind: "setTempo"; value: number; baseRev: number };

export interface MutationEntry {
  /** Per-client monotonic id from the pending queue — the idempotence key. */
  mutationId: number;
  /** Entries sharing one are a single user action; the merge treats them
   * all-or-nothing (step 4). */
  gestureId?: string;
  mutation: Mutation;
}

/** Client → room, the v4 REQUEST shape. Detected by `v: 4` — never a flag. */
export interface MutationEnvelope {
  v: 4;
  clientId: string;
  baseRoomRev: number;
  epoch?: string;
  /** Max mutationId in this envelope; at or below the stored watermark means
   * the whole envelope was already processed. */
  lastMutationId: number;
  mutations: MutationEntry[];
}

export function isMutationEnvelope(v: unknown): v is MutationEnvelope {
  if (typeof v !== "object" || v === null) return false;
  const e = v as Partial<MutationEnvelope>;
  return (
    e.v === 4 &&
    typeof e.clientId === "string" &&
    typeof e.lastMutationId === "number" &&
    Array.isArray(e.mutations)
  );
}

/** A room clip's wire payload — the ClipData fields and nothing else (no id,
 * trackId, audioKey or rev). Exported so every hand-rolled copy in the engine
 * (apply-ops' repair, viz's edits) goes through one place and a new field
 * cannot be silently dropped by one of them. */
export function clipDataOf(c: ClipData): ClipData {
  const out: ClipData = {
    filePath: c.filePath,
    startTime: c.startTime,
    duration: c.duration,
    isWarped: c.isWarped,
    looping: c.looping,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    name: c.name,
    color: c.color,
    muted: c.muted,
    warpModeValue: c.warpModeValue,
  };
  if (c.clipKind === "midi") {
    out.clipKind = "midi";
    if (c.notes !== undefined) out.notes = c.notes;
    if (c.notesDigest !== undefined) out.notesDigest = c.notesDigest;
  }
  if (c.slot !== undefined) out.slot = c.slot;
  if (c.slotLooping !== undefined) out.slotLooping = c.slotLooping;
  if (c.sourceTempo !== undefined) out.sourceTempo = c.sourceTempo;
  if (c.warpMarkers !== undefined) out.warpMarkers = c.warpMarkers.map((m) => ({ beat: m.beat, sec: m.sec }));
  if (c.transpose !== undefined) out.transpose = c.transpose;
  if (c.detune !== undefined) out.detune = c.detune;
  if (c.warpSettings !== undefined) out.warpSettings = warpSettingsOf(c.warpSettings) ?? {};
  if (c.libraryClip !== undefined) out.libraryClip = { id: c.libraryClip.id, ownerId: c.libraryClip.ownerId };
  if (c.render !== undefined) out.render = { ...(c.render.key !== undefined ? { key: c.render.key } : {}), hash: c.render.hash, tempo: c.render.tempo };
  return out;
}

/** A clip payload for a mutation whose target the room doesn't hold — the
 * merge rejects on the id before ever reading the payload. */
const MISSING_CLIP: ClipData = {
  filePath: "",
  startTime: 0,
  duration: 0,
  isWarped: false,
  looping: false,
  startMarker: 0,
  endMarker: 0,
  loopStart: 0,
  loopEnd: 0,
  name: "",
  color: 0,
  muted: false,
  warpModeValue: 0,
};

/**
 * Translate one mutation into the v3 intent that expresses it against `room`
 * AS IT IS NOW. For moveClip/editClip that means re-asserting the clip's
 * CURRENT canonical content with the change applied — which is exactly why a
 * move carries no audio: the content half of the upsert is the room's own,
 * key included (an update carrying `audioKey: null` keeps what the room has).
 */
export function mutationToIntent(m: Mutation, room: CanonicalRoom): Intent {
  switch (m.kind) {
    case "createTrack":
      return {
        kind: "createTrack",
        tempId: m.tempId,
        name: m.name,
        ord: m.ord,
        ...(m.trackKind !== undefined ? { trackKind: m.trackKind } : {}),
      };
    case "setTrackField":
      return {
        kind: "setTrackField",
        id: m.trackId,
        baseRev: m.baseRev,
        field: m.field,
        value: m.value,
      };
    case "deleteTrack":
      return { kind: "deleteTrack", id: m.trackId, baseRev: m.baseRev };
    case "setTrackOrder":
      return { kind: "setTrackOrder", id: m.trackId, baseRev: m.baseRev, ord: m.ord };
    case "setSceneNames":
      return { kind: "setSceneNames", baseRev: m.baseRev, names: m.names };
    case "setCuePoints":
      return { kind: "setCuePoints", baseRev: m.baseRev, points: m.points };
    case "setMaster":
      return { kind: "setMaster", baseRev: m.baseRev, volume: m.volume, pan: m.pan };
    case "setScale":
      return { kind: "setScale", baseRev: m.baseRev, scale: m.scale };
    case "setTrackSends":
      return { kind: "setTrackSends", id: m.trackId, baseRev: m.baseRev, sends: m.sends };
    case "setTrackDevices":
      return { kind: "setTrackDevices", id: m.trackId, baseRev: m.baseRev, devices: m.devices, ...(m.origin ? { origin: m.origin } : {}) };
    case "setDeviceParam":
      return { kind: "setDeviceParam", id: m.trackId, baseRev: m.baseRev, device: m.device, param: m.param, value: m.value };
    case "setChain":
      return { kind: "setChain", owner: m.owner, baseRev: m.baseRev, devices: m.devices, ...(m.origin ? { origin: m.origin } : {}) };
    case "setChainParam":
      return { kind: "setChainParam", owner: m.owner, baseRev: m.baseRev, device: m.device, param: m.param, value: m.value };
    case "setReturns":
      return { kind: "setReturns", baseRev: m.baseRev, returns: m.returns };
    case "createClip":
      return {
        kind: "upsertClip",
        id: null,
        tempId: m.tempId,
        trackId: m.trackId,
        baseRev: null,
        clip: m.clip,
        audioKey: m.audioKey,
      };
    case "moveClip": {
      const clip = room.clips.find((c) => c.id === m.clipId);
      return {
        kind: "upsertClip",
        id: m.clipId,
        tempId: `move:${m.clipId}`,
        // When the clip is gone, any REAL lane keeps the merge's track check
        // satisfied so the rejection names the honest problem: unknown-clip.
        trackId: m.trackId ?? clip?.trackId ?? room.tracks[0]?.id ?? "",
        baseRev: m.baseRev,
        clip: clip ? movedClipData(clip, m) : MISSING_CLIP,
        audioKey: null, // keep the room's — a move never re-renders
      };
    }
    case "editClip": {
      const clip = room.clips.find((c) => c.id === m.clipId);
      return {
        kind: "upsertClip",
        id: m.clipId,
        tempId: `edit:${m.clipId}`,
        trackId: clip?.trackId ?? room.tracks[0]?.id ?? "",
        baseRev: m.baseRev,
        clip: clip ? { ...editBase(clip, m.audioKey), ...m.patch } : MISSING_CLIP,
        audioKey: m.audioKey ?? null,
      };
    }
    case "deleteClip":
      return { kind: "deleteClip", id: m.clipId, baseRev: m.baseRev };
    case "setTempo":
      return { kind: "setTempo", baseRev: m.baseRev, value: m.value };
  }
}

/**
 * What an edit asserts beneath its patch. With NEW audio (a Live content
 * refresh: resize, consolidate, a bounce) the room's object-level fields —
 * the render, the pitch and knobs it baked, the warp markers and source
 * tempo — describe the OLD object, so they are left out unless the patch
 * says them: the merge then applies its new-audio rules (render dropped,
 * pitch reset under a render, markers dropped, source tempo stamped).
 * Re-asserting them kept a stale render and re-applied a pitch the new
 * audio already carried (2026-10-27 audit: transpose 3 on a consolidated
 * render-built clip, played and re-rendered pitched twice).
 */
function editBase(clip: ClipData & { audioKey?: string | null }, audioKey: string | null | undefined): ClipData {
  const base = clipDataOf(clip);
  if (audioKey === undefined || audioKey === null || audioKey === clip.audioKey) return base;
  delete base.render;
  delete base.warpMarkers;
  delete base.transpose;
  delete base.detune;
  delete base.warpSettings;
  delete base.sourceTempo;
  return base;
}

/** The clip's content with a move applied. A slot move lands in Session
 * View at startTime 0; a `null` slot lands on the timeline; an absent slot
 * keeps the clip's current view. */
function movedClipData(clip: ClipData, m: Extract<Mutation, { kind: "moveClip" }>): ClipData {
  const out = clipDataOf(clip);
  if (m.slot === undefined) {
    out.startTime = m.startTime;
    return out;
  }
  if (m.slot === null) {
    delete out.slot;
    out.startTime = m.startTime;
  } else {
    out.slot = m.slot;
    out.startTime = 0;
  }
  return out;
}

/**
 * Translate a v4 envelope's entries for the merge core, carrying each entry's
 * mutationId and gestureId on the intent (the watermark filter and the
 * gesture-atomic merge read them there, same as v3).
 */
export function mutationsToIntents(entries: MutationEntry[], room: CanonicalRoom): Intent[] {
  return entries.map((e) => ({
    ...mutationToIntent(e.mutation, room),
    mutationId: e.mutationId,
    ...(e.gestureId !== undefined ? { gestureId: e.gestureId } : {}),
  }));
}

/**
 * `mutation` with its baseRev updated to `room`'s current rev — the mutation
 * flavor of the send-time refresh (see projection.ts).
 */
export function withRefreshedMutationBaseRev(m: Mutation, room: CanonicalRoom): Mutation {
  switch (m.kind) {
    case "setTrackField":
    case "setTrackOrder":
    case "deleteTrack": {
      const t = room.tracks.find((x) => x.id === m.trackId);
      return t ? { ...m, baseRev: t.rev } : m;
    }
    case "moveClip":
    case "editClip":
    case "deleteClip": {
      const c = room.clips.find((x) => x.id === m.clipId);
      return c ? { ...m, baseRev: c.rev } : m;
    }
    case "setTempo":
      return { ...m, baseRev: room.tempoRev };
    case "setSceneNames":
      return { ...m, baseRev: room.scenesRev ?? 0 };
    case "setCuePoints":
      return { ...m, baseRev: room.cuePointsRev ?? 0 };
    case "setMaster":
      return { ...m, baseRev: room.masterRev ?? 0 };
    case "setScale":
      return { ...m, baseRev: room.scaleRev ?? 0 };
    case "setTrackSends": {
      const t = room.tracks.find((x) => x.id === m.trackId);
      return t ? { ...m, baseRev: t.rev } : m;
    }
    case "setTrackDevices":
    case "setDeviceParam": {
      const t = room.tracks.find((x) => x.id === m.trackId);
      return t ? { ...m, baseRev: t.devicesRev ?? 0 } : m;
    }
    case "setChain":
    case "setChainParam":
      return { ...m, baseRev: room.chains?.[m.owner]?.rev ?? 0 };
    case "setReturns":
      return { ...m, baseRev: room.returnsRev ?? 0 };
    case "createTrack":
    case "createClip":
      return m;
  }
}

/**
 * Map a rejection from the merge core back to the mutation entry it came from
 * (rejections cite the TRANSLATED intent; the client's pending queue speaks
 * mutationIds).
 */
export function rejectedMutationIds(rejected: Rejection[]): number[] {
  return rejected
    .map((r) => r.intent.mutationId)
    .filter((id): id is number => id !== undefined);
}

// ---------------------------------------------------------------------------
// Compiler output → mutations (the client's outbound half)
// ---------------------------------------------------------------------------

/** A compiled mutation plus what still needs filling in. */
export interface CompiledMutation {
  mutation: Mutation;
  gestureId?: string;
}

export interface MutationCompileResult {
  items: CompiledMutation[];
  /** Same shape as compile-intents' AudioRequest, re-indexed into `items`. */
  needsAudio: Array<{ intentIndex: number; trackIndex: number; clip: CanonicalClipShape }>;
}

// Structurally CanonicalClip from canonical.ts, kept loose here to avoid the
// import cycle (canonical → nothing; compile-intents → canonical + this).
interface CanonicalClipShape {
  name: string;
  startTime: number;
  duration: number;
  startMarker: number;
  endMarker: number;
  looping: boolean;
  loopStart: number;
  loopEnd: number;
  color: number;
  muted: boolean;
  filePath: string;
  warping: boolean;
  warpMode: { value: number };
  clipKind?: ClipKind;
  notes?: WireNote[];
  notesDigest?: string;
  slot?: number;
}

/**
 * Turn the compiler's v3 intents into named mutations. The load-bearing case:
 * a gestureId PAIR (bound deleteClip + unbound create — the compiler's move
 * evidence) collapses into ONE `moveClip`, which is where the v3 symptoms die
 * at the root — it cannot half-apply, and it carries no audio, so the pair's
 * pending render request is DROPPED here, before any bytes are rendered or
 * uploaded. Only id-carried moves get this: a positionally-inferred change
 * (equal-length permutation, bootstrap-era) still flows as an edit with an
 * audio refresh — the 2026-08-04 rule.
 */
export function intentsToMutations(
  compiled: {
    intents: Intent[];
    needsAudio: Array<{ intentIndex: number; trackIndex: number; clip: CanonicalClipShape }>;
  },
  room: CanonicalRoom,
): MutationCompileResult {
  const audioByIntent = new Map(compiled.needsAudio.map((n) => [n.intentIndex, n]));

  // Move pairs: exactly one deleteClip + one unbound create sharing a gestureId.
  const byGesture = new Map<string, number[]>();
  compiled.intents.forEach((intent, i) => {
    if (intent.gestureId !== undefined) {
      const arr = byGesture.get(intent.gestureId) ?? [];
      arr.push(i);
      byGesture.set(intent.gestureId, arr);
    }
  });
  const pairedIndex = new Map<number, { deleteAt: number; createAt: number }>();
  for (const indices of byGesture.values()) {
    if (indices.length !== 2) continue;
    const [a, b] = indices.map((i) => compiled.intents[i]!);
    const del = a!.kind === "deleteClip" ? indices[0]! : b!.kind === "deleteClip" ? indices[1]! : -1;
    const create =
      a!.kind === "upsertClip" && a!.id === null
        ? indices[0]!
        : b!.kind === "upsertClip" && b!.id === null
          ? indices[1]!
          : -1;
    if (del === -1 || create === -1) continue;
    pairedIndex.set(del, { deleteAt: del, createAt: create });
    pairedIndex.set(create, { deleteAt: del, createAt: create });
  }

  const items: CompiledMutation[] = [];
  const needsAudio: MutationCompileResult["needsAudio"] = [];
  const emitted = new Set<number>();

  compiled.intents.forEach((intent, i) => {
    if (emitted.has(i)) return;
    const pair = pairedIndex.get(i);
    if (pair) {
      emitted.add(pair.deleteAt);
      emitted.add(pair.createAt);
      const del = compiled.intents[pair.deleteAt]!;
      const create = compiled.intents[pair.createAt]!;
      if (del.kind !== "deleteClip" || create.kind !== "upsertClip") return;
      const roomClip = room.clips.find((c) => c.id === del.id);
      // A slot→slot move names the target slot; a timeline move says nothing
      // about slots (so old servers read it exactly as before).
      const sessionMove = create.clip.slot !== undefined || roomClip?.slot !== undefined;
      items.push({
        mutation: {
          kind: "moveClip",
          clipId: del.id,
          startTime: create.clip.startTime,
          ...(roomClip && create.trackId !== roomClip.trackId
            ? { trackId: create.trackId }
            : {}),
          ...(sessionMove ? { slot: create.clip.slot ?? null } : {}),
          baseRev: del.baseRev,
        },
      });
      // The create's render request dies with the pair — a move needs no bytes.
      return;
    }

    emitted.add(i);
    const audio = audioByIntent.get(i);
    const push = (mutation: Mutation): void => {
      items.push({
        mutation,
        ...(intent.gestureId !== undefined ? { gestureId: intent.gestureId } : {}),
      });
      if (audio) {
        needsAudio.push({ ...audio, intentIndex: items.length - 1 });
      }
    };
    switch (intent.kind) {
      case "createTrack":
        push({
          kind: "createTrack",
          tempId: intent.tempId,
          name: intent.name,
          ord: intent.ord,
          ...(intent.trackKind !== undefined ? { trackKind: intent.trackKind } : {}),
        });
        break;
      case "setTrackField":
        push({
          kind: "setTrackField",
          trackId: intent.id,
          field: intent.field,
          value: intent.value,
          baseRev: intent.baseRev,
        });
        break;
      case "deleteTrack":
        push({ kind: "deleteTrack", trackId: intent.id, baseRev: intent.baseRev });
        break;
      case "setTrackOrder":
        push({ kind: "setTrackOrder", trackId: intent.id, ord: intent.ord, baseRev: intent.baseRev });
        break;
      case "setSceneNames":
        push({ kind: "setSceneNames", names: intent.names, baseRev: intent.baseRev });
        break;
      case "setCuePoints":
        push({ kind: "setCuePoints", points: intent.points, baseRev: intent.baseRev });
        break;
      case "setMaster":
        push({ kind: "setMaster", volume: intent.volume, pan: intent.pan, baseRev: intent.baseRev });
        break;
      case "setScale":
        push({ kind: "setScale", scale: intent.scale, baseRev: intent.baseRev });
        break;
      case "setTrackSends":
        push({ kind: "setTrackSends", trackId: intent.id, sends: intent.sends, baseRev: intent.baseRev });
        break;
      case "setTrackDevices":
        push({ kind: "setTrackDevices", trackId: intent.id, devices: intent.devices, baseRev: intent.baseRev, ...(intent.origin ? { origin: intent.origin } : {}) });
        break;
      case "setDeviceParam":
        push({ kind: "setDeviceParam", trackId: intent.id, device: intent.device, param: intent.param, value: intent.value, baseRev: intent.baseRev });
        break;
      case "setChain":
        push({ kind: "setChain", owner: intent.owner, devices: intent.devices, baseRev: intent.baseRev, ...(intent.origin ? { origin: intent.origin } : {}) });
        break;
      case "setChainParam":
        push({ kind: "setChainParam", owner: intent.owner, device: intent.device, param: intent.param, value: intent.value, baseRev: intent.baseRev });
        break;
      case "setReturns":
        push({ kind: "setReturns", returns: intent.returns, baseRev: intent.baseRev });
        break;
      case "upsertClip":
        if (intent.id === null) {
          push({
            kind: "createClip",
            tempId: intent.tempId,
            trackId: intent.trackId,
            clip: intent.clip,
            audioKey: intent.audioKey,
          });
        } else {
          push({
            kind: "editClip",
            clipId: intent.id,
            patch: intent.clip,
            ...(intent.audioKey !== null ? { audioKey: intent.audioKey } : {}),
            baseRev: intent.baseRev ?? 0,
          });
        }
        break;
      case "deleteClip":
        push({ kind: "deleteClip", clipId: intent.id, baseRev: intent.baseRev });
        break;
      case "setTempo":
        push({ kind: "setTempo", value: intent.value, baseRev: intent.baseRev });
        break;
    }
  });

  return { items, needsAudio };
}

/**
 * Fill in the audio a compiled batch was waiting on, and DROP any mutation
 * whose bytes couldn't be produced — with its whole gesture, same as
 * fillAudioKeys. A createClip/editClip is never sent with missing bytes, and
 * never becomes a deletion.
 */
export function fillMutationAudio(
  items: CompiledMutation[],
  needsAudio: MutationCompileResult["needsAudio"],
  prepared: Array<{ objectKey: string; clip: ClipData } | null>,
): { sendable: CompiledMutation[]; dropped: string[] } {
  const slots: Array<CompiledMutation | null> = [...items];
  const dropped: string[] = [];
  const doomedGestures = new Set<string>();

  needsAudio.forEach((need, i) => {
    const item = slots[need.intentIndex];
    if (!item) return;
    const m = item.mutation;
    if (m.kind !== "createClip" && m.kind !== "editClip") return;
    const got = prepared[i];
    if (!got) {
      // A MIDI clip's bytes are an optional bounce: no bytes means it ships
      // as notes only, never that it is dropped (a notes-only clip is a
      // complete clip — see merge-engine's create rule).
      if ((m.kind === "createClip" ? m.clip : m.patch).clipKind === "midi") return;
      dropped.push(
        need.clip.slot !== undefined
          ? `"${need.clip.name}"[slot ${need.clip.slot}]`
          : `"${need.clip.name}"@${need.clip.startTime}`,
      );
      slots[need.intentIndex] = null;
      if (item.gestureId !== undefined) doomedGestures.add(item.gestureId);
      return;
    }
    slots[need.intentIndex] =
      m.kind === "createClip"
        ? { ...item, mutation: { ...m, clip: withMidiPayload(got.clip, m.clip), audioKey: got.objectKey } }
        : { ...item, mutation: { ...m, patch: withMidiPayload(got.clip, m.patch as ClipData), audioKey: got.objectKey } };
  });

  if (doomedGestures.size > 0) {
    slots.forEach((item, i) => {
      if (!item || item.gestureId === undefined) return;
      if (!doomedGestures.has(item.gestureId)) return;
      slots[i] = null;
      dropped.push(`${item.mutation.kind} (gesture ${item.gestureId} lost its audio)`);
    });
  }

  return { sendable: slots.filter((s): s is CompiledMutation => s !== null), dropped };
}
